import { useMutation, useQuery } from "@apollo/client";
import { GridColDef } from "@mui/x-data-grid";
import { ClientAllocationRow } from "_graphql-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNotify } from "react-admin";
import { CLIENT_ALLOCATIONS, SAVE_CLIENT_MONTH_ENTRIES } from "./graphql";
import {
  emptyFields,
  formatMoney,
  formatMonth,
  isDraftChanged,
  isEditableMonthField,
  liveReturns,
  monthArg,
  MonthEntryDraft,
  MonthEntryField,
  monthEntryInitial,
  monthEntryInputs,
  totals,
} from "./logic";
import {
  columnGroups,
  confirmDiscard,
  EmptyState,
  EntryInput,
  FilterBar,
  groupCol,
  groupStarts,
  moneyCol,
  MonthField,
  MultiFundPicker,
  Page,
  percentCol,
  QueryState,
  ReadOnlyCell,
  ReportGrid,
  SaveActions,
  Section,
  textCol,
  TOTAL_ROW_ID,
  useSelectedFunds,
  useSelectedMonth,
} from "./shared";

interface EntryRow
  extends Pick<
    ClientAllocationRow,
    "rcgFundName" | "clientName" | "shareClassName" | "begBal" | "addition" | "redemption" | "switchIn" | "switchOut" | "adjBegBal" | "estNetEndBal" | "derived"
  > {
  id: number | string;
  draft: MonthEntryDraft;
  initial: MonthEntryDraft;
  actGrossReturn: number | null;
  actNetReturn: number | null;
}

const SUMMED = ["begBal", "addition", "redemption", "switchIn", "switchOut", "adjBegBal", "estNetEndBal"] as const;

const numberOr0 = (text: string) => {
  const n = Number(text.trim().replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

export function MonthEndEntry() {
  const notify = useNotify();
  const [fundIds, setFundIds] = useSelectedFunds();
  const [month, setMonth] = useSelectedMonth();
  const [drafts, setDrafts] = useState<ReadonlyMap<number, MonthEntryDraft>>(new Map());
  const { data, loading, error, refetch } = useQuery(CLIENT_ALLOCATIONS, {
    variables: { rcgFundIds: fundIds, month: monthArg(month) },
    skip: fundIds.length === 0,
  });
  const [save, saving] = useMutation(SAVE_CLIENT_MONTH_ENTRIES);
  useEffect(() => setDrafts(new Map()), [fundIds, month]);

  const allocations = data?.clientAllocations ?? [];

  const setField = useCallback(
    (row: EntryRow, field: MonthEntryField, value: string) =>
      setDrafts(prev => new Map(prev).set(row.id as number, { ...(prev.get(row.id as number) ?? row.initial), [field]: value })),
    []
  );

  const rows = useMemo((): EntryRow[] => {
    const body = allocations.map((a): EntryRow => {
      const initial = monthEntryInitial(a);
      const draft = drafts.get(a.shareClassId) ?? initial;
      const live = liveReturns(a, draft);
      return {
        id: a.shareClassId,
        rcgFundName: a.rcgFundName,
        clientName: a.clientName,
        shareClassName: a.shareClassName,
        derived: a.derived,
        begBal: a.begBal,
        addition: a.addition,
        redemption: a.redemption,
        switchIn: a.switchIn,
        switchOut: a.switchOut,
        adjBegBal: a.adjBegBal,
        estNetEndBal: a.estNetEndBal,
        draft,
        initial,
        actGrossReturn: live.gross,
        actNetReturn: live.net,
      };
    });
    if (!body.length) return [];
    const sums = totals(body, SUMMED);
    const entrySum = (f: MonthEntryField) => String(body.reduce((s, r) => s + numberOr0(r.draft[f]), 0));
    const totalDraft: MonthEntryDraft = {
      actualEndBal: entrySum("actualEndBal"),
      mgmtFee: entrySum("mgmtFee"),
      adminFee: entrySum("adminFee"),
      incentiveFee: entrySum("incentiveFee"),
      finalGrossReturn: "",
      finalNetReturn: "",
    };
    return [
      ...body,
      {
        id: TOTAL_ROW_ID,
        rcgFundName: "Total",
        clientName: "",
        shareClassName: "",
        derived: true,
        ...sums,
        draft: totalDraft,
        initial: totalDraft,
        actGrossReturn: null,
        actNetReturn: null,
      },
    ];
  }, [allocations, drafts]);

  const pending = rows.filter(r => r.id !== TOTAL_ROW_ID && isDraftChanged(r.draft, r.initial)).length;
  const starts = useMemo(() => groupStarts(rows, r => r.rcgFundName), [rows]);
  // Flows nobody had this month are columns of blanks; with none at all, Adj. Beg. Bal is Beg. Bal again.
  const hidden = useMemo((): ReadonlySet<string> => {
    const empty: Set<string> = emptyFields(rows, ["addition", "redemption", "switchIn", "switchOut"]);
    if (empty.size === 4) empty.add("adjBegBal");
    return empty;
  }, [rows]);

  const columns = useMemo((): GridColDef<EntryRow>[] => {
    const entryCol = (field: MonthEntryField, headerName: string, percent = false): GridColDef<EntryRow> => ({
      field: `entry.${field}`,
      headerName,
      width: percent ? 120 : 140,
      align: "right",
      headerAlign: "right",
      sortable: false,
      valueGetter: (_v, row) => row.draft[field],
      renderCell: ({ row }) => {
        if (row.id === TOTAL_ROW_ID) return percent ? "" : formatMoney(numberOr0(row.draft[field]));
        if (!isEditableMonthField(row, field)) {
          return <ReadOnlyCell
              // Only money fields are ever read-only here (a derived class keeps its final returns).
              text={row.draft[field] === "" ? "" : formatMoney(Number(row.draft[field]))}
              reason="Derived share class: only the final returns can be entered" />;
        }
        return (
          <EntryInput
            value={row.draft[field]}
            initial={row.initial[field]}
            onChange={v => setField(row, field, v)}
            suffix={percent ? "%" : undefined}
            label={`${row.shareClassName} ${headerName}`}
            title={field === "mgmtFee" || field === "adminFee" || field === "incentiveFee" ? "Blank: calculated from the fee schedule" : undefined}
          />
        );
      },
    });
    const all: GridColDef<EntryRow>[] = [
      groupCol<EntryRow>("rcgFundName", "RCG Fund", starts, 240),
      textCol<EntryRow>("clientName", "Client", 220),
      textCol<EntryRow>("shareClassName", "Share Class", 110),
      moneyCol<EntryRow>("begBal", "Beg. Bal"),
      moneyCol<EntryRow>("addition", "Addition"),
      moneyCol<EntryRow>("redemption", "Redemption"),
      moneyCol<EntryRow>("switchIn", "Switch In"),
      moneyCol<EntryRow>("switchOut", "Switch Out"),
      moneyCol<EntryRow>("adjBegBal", "Adj. Beg. Bal"),
      entryCol("mgmtFee", "Mgmt Fee $"),
      entryCol("adminFee", "Admin Fee $"),
      entryCol("incentiveFee", "Perf Fee $"),
      moneyCol<EntryRow>("estNetEndBal", "Est. Net End Bal", 140),
      entryCol("actualEndBal", "Act. Net End Bal $"),
      percentCol<EntryRow>("actGrossReturn", "Act. Gross %"),
      percentCol<EntryRow>("actNetReturn", "Act. Net %"),
      entryCol("finalGrossReturn", "Final Gross %", true),
      entryCol("finalNetReturn", "Final Net %", true),
    ];
    return all.filter(c => !hidden.has(c.field));
  }, [setField, hidden, starts]);

  const groups = useMemo(
    () =>
      columnGroups(columns, [
        { groupId: "Balances", fields: ["begBal", "addition", "redemption", "switchIn", "switchOut", "adjBegBal"] },
        { groupId: "Fees", fields: ["entry.mgmtFee", "entry.adminFee", "entry.incentiveFee"] },
        { groupId: "End Balance", fields: ["estNetEndBal", "entry.actualEndBal"] },
        { groupId: "Returns", fields: ["actGrossReturn", "actNetReturn", "entry.finalGrossReturn", "entry.finalNetReturn"] },
      ]),
    [columns]
  );

  const onSave = async () => {
    try {
      const entries = monthEntryInputs(allocations, drafts);
      if (entries.length === 0) {
        notify("Nothing has changed", { type: "info" });
        return;
      }
      await save({ variables: { month: monthArg(month), entries } });
      await refetch();
      setDrafts(new Map());
      notify(`Saved ${entries.length} share class${entries.length === 1 ? "" : "es"}`, { type: "success" });
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), { type: "error" });
    }
  };

  return (
    <Page
      title="Actual EndBal and Fee Entry"
      description="Blank clears a value. Fees are as entered (blank: calculated from the fee schedule). Derived share classes take only final returns. Changed cells are highlighted until saved."
    >
      <FilterBar>
        <MultiFundPicker value={fundIds} onChange={ids => confirmDiscard(pending) && setFundIds(ids)} />
        <MonthField value={month} onChange={m => confirmDiscard(pending) && setMonth(m)} />
      </FilterBar>
      <QueryState loading={loading} error={error} />
      {fundIds.length === 0 ? (
        <EmptyState>Pick one or more funds, or add a whole group, to enter their month-end balances and fees.</EmptyState>
      ) : (
        <Section
          title={`Share classes, ${formatMonth(monthArg(month))}`}
          actions={<SaveActions pending={pending} saving={saving.loading} onSave={onSave} onDiscard={() => setDrafts(new Map())} />}
        >
          <ReportGrid<EntryRow>
            rows={rows}
            columns={columns}
            columnGroupingModel={groups}
            groupStarts={starts}
            noRowsLabel="No share classes in these funds for this month"
          />
        </Section>
      )}
    </Page>
  );
}
